import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { CheckCircle, RestartAlt } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../Auth/authSlice';
import { useFetchProfileQuery } from '../../Profile/profileApiSlice';
import { extractPrefill, resolveUser } from '../gplPrefill';
import { variants } from '../../../variants/variants';
import gplIcon from './gplLogo.png';
import comingSoonGif from './comingSoon.gif';
import {
  checkEmailAvailable,
  extractErrorText,
  extractFieldErrors,
  extractIntegrationError,
  extractPlayerPhoto,
  fetchLookups,
  fetchPlayerByEmail,
  fetchRegistrationWindow,
  mapPlayerToFormValues,
  registerPlayer,
} from '../gplApi';

// Fields that come from the employee record and are therefore not editable here.
// Gender included: it decides which auction the player enters, so it should match HR
// data rather than being self-selected. Both options still render - see radioRow.
const LOCKED_FIELDS = ['FirstName', 'LastName', 'EmailId', 'PhoneNo', 'Dob', 'Gender'];

// Nothing in this form can be edited once it is submitted, so both end screens point the
// player at the person who can change it for them.
const GPL_CONTACT = 'Abhishek Pandey (abhishek.pandey@thegatewaycorp.com)';

// Every dropdown shares one popup config. Without a height cap a 10-option list (the
// experience years) grows tall enough that MUI has to reposition it over the form,
// so it lands nowhere near its own field and looks nothing like the short lists.
const SELECT_MENU_PROPS = {
  PaperProps: { sx: { maxHeight: 260 } },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

// "Have you ever played GPL In Past?" - ExperienceYears is an int 0-9 where 0 is "No".
const EXPERIENCE_YEARS = [
  { value: 0, label: 'No' },
  ...Array.from({ length: 9 }, (_, i) => ({ value: i + 1, label: `${i + 1} year${i === 0 ? '' : 's'}` })),
];

// The 1-5 scale under the rating sliders. MUI draws a label under each mark when `marks`
// is given objects rather than `true`, so the numbers come from the same source as the
// tick positions and cannot drift out of step with them.
const RATING_MARKS = [1, 2, 3, 4, 5].map((value) => ({ value, label: String(value) }));

// The hype rating is presentation only - the reference form validates it but keeps it
// out of the submitted player payload, and the API has no field for it.
const HYPE_EMOJIS = [
  { emoji: '😴', tooltip: 'Meh, not really feeling it.' },
  { emoji: '😐', tooltip: "It's okay, I guess." },
  { emoji: '😅', tooltip: "I might watch if I'm free!" },
  { emoji: '😄', tooltip: "Can't wait to catch the matches! 🎉" },
  { emoji: '🤩', tooltip: "I'm all in! Let's go for it! 🙌🏏" },
];

// Which roles unlock the batting / bowling halves of the form. The reference app keys
// these off role GUIDs; matching on the role name survives the GUIDs being reissued.
const BAT_ROLES = ['batsman', 'wicketkeeper batsman', 'all-rounder'];
// A wicketkeeper batsman keeps and bats but does not bowl, so the bowling half stays out
// for that role - only a bowler or an all-rounder unlocks it.
const BALL_ROLES = ['bowler', 'all-rounder'];

// Formik field names match the API field names exactly so a 400 validation
// body can be mapped straight back onto the fields.
const INITIAL_VALUES = {
  FirstName: '',
  LastName: '',
  EmailId: '',
  PhoneNo: '',
  Dob: '',
  Gender: '',
  TshirtSizeExternalId: '',
  WorkLocationExternalId: '',
  PlayerRoleExternalId: '',
  BattingStyleExternalId: '',
  BatRating: '',
  Runs: '',
  BowlingStyleExternalId: '',
  BowlRating: '',
  Wickets: '',
  TShirtNumber: '',
  TShirtName: '',
  ExperienceYears: '',
  CricHeroesAccount: '',
  CricHeroesUserId: '',
  CricketJourney: '',
  MedicalConditions: '',
  MedicalDetails: '',
};

const FIELD_NAMES = Object.keys(INITIAL_VALUES);

const NAME_RULE = /^[A-Za-z]+$/;

// Cricketing journey and medical details are the two free-text answers. Kept as one
// constant because the limit is enforced in three places - the Yup rule, the input's own
// maxLength and the character counter - and they must never disagree.
const FREE_TEXT_MAX = 400;

// The jersey name is deliberately looser than NAME_RULE. FirstName / LastName are single
// tokens the API caps at 20 letters, but this is a display name printed on a shirt and is
// commonly a full name ('Tulsi Chhatriwala') or a hyphenated / apostrophised one. What it
// must not be is a number - the jersey number is its own field, and the API accepts
// anything here, so this is the only guard against a registration named '12345'.
const JERSEY_NAME_RULE = /^[A-Za-z][A-Za-z '.-]*$/;
const PHONE_RULE = /^[6-9][0-9]{9}$/;

// The API only accepts letters (max 20) for names, so drop anything else the
// employee record may carry (initials, dots, spaces) rather than prefilling a value
// that would immediately fail validation.
const toApiName = (value) => (value || '').replace(/[^A-Za-z]/g, '').slice(0, 20);

// Employee records store numbers in mixed formats (+91, spaces, hyphens); keep the
// last 10 digits, which is what the API expects.
const toApiPhone = (value) => {
  const digits = (value || '').toString().replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
};

// The API takes the photo as a file part, but a kept profile photo is only a URL, so
// read it back into a File. playerImg is optional, so a failure here (CORS, offline)
// must never block the registration itself.
const fileFromUrl = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) return null;
    return new File([blob], `profile.${blob.type.split('/')[1] || 'jpg'}`, { type: blob.type });
  } catch (error) {
    return null;
  }
};

const scoreRule = (label) =>
  Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .typeError(`${label} must be a number`)
    .integer(`${label} must be a whole number`)
    .min(0, `${label} must be between 0 and 999`)
    .max(999, `${label} must be between 0 and 999`);

const GplRegistrationForm = () => {
  const theme = useTheme();
  // Follow the application's own theme rather than the standalone form's orange.
  const ACCENT = theme.palette.primary.main;
  const reduxUser = useSelector(selectCurrentUser);

  // Redux is localStorage-backed via redux-persist; fall back to reading the persisted
  // copy directly in case this renders before the store has rehydrated.
  const authUser = useMemo(() => resolveUser(reduxUser), [reduxUser]);

  // The employee profile API is the authoritative source and the only one that resolves
  // the photo URL; the persisted login payload covers the text fields until it arrives.
  const { data: profile } = useFetchProfileQuery(authUser?.empCode, { skip: !authUser?.empCode });

  const prefill = useMemo(() => extractPrefill(profile, authUser), [profile, authUser]);

  const prefilledValues = useMemo(
    () => ({
      ...INITIAL_VALUES,
      FirstName: toApiName(prefill.firstName),
      LastName: toApiName(prefill.lastName),
      EmailId: prefill.email,
      PhoneNo: toApiPhone(prefill.phone),
      Dob: prefill.age,
      Gender: prefill.gender,
    }),
    [prefill]
  );

  // The employee's existing profile photo, shown until they upload their own.
  const profilePhotoUrl = prefill.photo;
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [registrationWindow, setRegistrationWindow] = useState(null);
  const [lookups, setLookups] = useState({
    roles: [],
    battingStyles: [],
    bowlingStyles: [],
    tShirtSizes: [],
    workLocations: [],
  });
  const [hypeIndex, setHypeIndex] = useState(null);
  const [hypeError, setHypeError] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  // Whichever email turned out to be registered - the employee's own, or one typed into
  // an unlocked field. The lookup must use that exact address, not the prefill.
  const [registeredEmail, setRegisteredEmail] = useState('');
  // Whether the "is this email already registered?" question has been answered yet. A
  // closed window must not be reported to someone who turns out to be registered, so
  // that screen waits on this rather than racing it.
  const [registrationChecked, setRegistrationChecked] = useState(false);
  // The submitted record, shown back to the player. Held apart from the registration
  // flow's own state: failing to load it must never stop the "already registered" card
  // itself from rendering.
  const [registeredPlayer, setRegisteredPlayer] = useState(null);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [playerError, setPlayerError] = useState('');

  const loadFormData = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const [windowData, lookupData] = await Promise.all([fetchRegistrationWindow(), fetchLookups()]);
      setRegistrationWindow(windowData);
      setLookups(lookupData);
    } catch (error) {
      setLoadError('Could not load the registration form. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFormData();
  }, [loadFormData]);


  // Both options stay on offer. Choosing one whose auction is closed swaps the form for
  // the coming-soon screen (see the registration-window checks below) instead of hiding
  // the option, so the player can see why and pick again.
  const genderOptions = [
    { value: 'Male', label: '👨 Male' },
    { value: 'Female', label: '👩 Female' },
  ];

  const roleNameFor = useCallback(
    (externalId) => lookups.roles.find((role) => role.value === externalId)?.label?.toLowerCase() || '',
    [lookups.roles]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        FirstName: Yup.string()
          .trim()
          .matches(NAME_RULE, 'Letters only, no spaces or digits')
          .max(20, 'Maximum 20 characters')
          .required('First name is required'),
        LastName: Yup.string()
          .trim()
          .matches(NAME_RULE, 'Letters only, no spaces or digits')
          .max(20, 'Maximum 20 characters')
          .required('Last name is required'),
        EmailId: Yup.string()
          .max(80, 'Maximum 80 characters')
          .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email format')
          .required('Official email address is required'),
        PhoneNo: Yup.string()
          .matches(PHONE_RULE, 'Must be 10 digits starting with 6-9')
          .required('Mobile number is required'),
        Dob: Yup.number()
          .transform((value, original) => (original === '' ? undefined : value))
          .typeError('Age must be a number')
          .integer('Age must be a whole number')
          .min(15, 'You must be at least 15 years old')
          .max(99, 'Enter a valid age')
          .required('Age is required'),
        Gender: Yup.string().required('Please select your gender'),
        TshirtSizeExternalId: Yup.string().required('T-Shirt size is required'),
        WorkLocationExternalId: Yup.string().required('Work location is required'),
        PlayerRoleExternalId: Yup.string().required('Role is required'),

        // Batting half - only when the chosen role bats.
        BattingStyleExternalId: Yup.string().when('PlayerRoleExternalId', {
          is: (roleId) => BAT_ROLES.includes(roleNameFor(roleId)),
          then: (schema) => schema.required('Please select batting style'),
          otherwise: (schema) => schema,
        }),
        BatRating: Yup.number()
          .transform((value, original) => (original === '' ? undefined : value))
          .min(1)
          .max(5),
        Runs: scoreRule('Runs').when('PlayerRoleExternalId', {
          is: (roleId) => BAT_ROLES.includes(roleNameFor(roleId)),
          // Only the empty case: a non-number or out-of-range value still gets its own
          // message from scoreRule, which does describe what is wrong with the value.
          then: (schema) => schema.required('Runs are required'),
          otherwise: (schema) => schema,
        }),

        // Bowling half - only when the chosen role bowls.
        BowlingStyleExternalId: Yup.string().when('PlayerRoleExternalId', {
          is: (roleId) => BALL_ROLES.includes(roleNameFor(roleId)),
          then: (schema) => schema.required('Please select bowling style'),
          otherwise: (schema) => schema,
        }),
        BowlRating: Yup.number()
          .transform((value, original) => (original === '' ? undefined : value))
          .min(1)
          .max(5),
        Wickets: scoreRule('Wickets').when('PlayerRoleExternalId', {
          is: (roleId) => BALL_ROLES.includes(roleNameFor(roleId)),
          then: (schema) => schema.required('Wickets are required'),
          otherwise: (schema) => schema,
        }),

        TShirtNumber: Yup.string()
          .trim()
          .matches(/^[0-9]{1,3}$/, 'Enter a number up to 3 digits')
          .required('Jersey number is required'),
        TShirtName: Yup.string()
          .trim()
          // excludeEmptyString so a blank field falls through to .required() and says so,
          // rather than reporting the format of a value that was never entered (GPL-10).
          .matches(JERSEY_NAME_RULE, {
            message: 'Letters, spaces, hyphens and apostrophes only',
            excludeEmptyString: true,
          })
          .max(20, 'Maximum 20 characters')
          .required('Name on jersey is required'),
        ExperienceYears: Yup.number()
          .transform((value, original) => (original === '' ? undefined : value))
          .typeError('Please select an option')
          .min(0)
          .max(9)
          .required('Please select an option'),
        CricHeroesAccount: Yup.string().required('Please select an option'),
        CricHeroesUserId: Yup.string().when('CricHeroesAccount', {
          is: 'Yes',
          then: (schema) =>
            schema
              .matches(PHONE_RULE, 'Must be 10 digits starting with 6-9')
              .required('Registered phone number is required'),
          otherwise: (schema) => schema,
        }),
        CricketJourney: Yup.string()
          .trim()
          .max(FREE_TEXT_MAX, `Maximum ${FREE_TEXT_MAX} characters`)
          .required('Please brief your cricketing journey'),
        MedicalConditions: Yup.string().required('Please select an option'),
        MedicalDetails: Yup.string().when('MedicalConditions', {
          is: 'Yes',
          then: (schema) =>
            schema
              .trim()
              .max(FREE_TEXT_MAX, `Maximum ${FREE_TEXT_MAX} characters`)
              .required('Medical details are required'),
          otherwise: (schema) => schema,
        }),
      }),
    [roleNameFor]
  );

  const formik = useFormik({
    // Seeded synchronously from the persisted login payload. Values that only arrive
    // later (from the profile API) are merged by the effect below rather than via
    // enableReinitialize, which would discard whatever the user had already typed.
    initialValues: prefilledValues,
    validationSchema,
    onSubmit: async (values, { setFieldError, resetForm }) => {
      if (hypeIndex === null) {
        setHypeError('Please pick how hyped you are');
        return;
      }

      try {
        // Derive the abilities from the values being submitted rather than from
        // render-time state, so the payload always matches the chosen role.
        const submittedRole = roleNameFor(values.PlayerRoleExternalId);
        const submittedCanBat = BAT_ROLES.includes(submittedRole);
        const submittedCanBall = BALL_ROLES.includes(submittedRole);

        const payload = { ...values };
        // Drop values from halves of the form that do not apply to the chosen role,
        // and from conditional questions the user answered "No" to.
        if (!submittedCanBat) {
          payload.BattingStyleExternalId = '';
          payload.BatRating = '';
          payload.Runs = '';
        }
        if (!submittedCanBall) {
          payload.BowlingStyleExternalId = '';
          payload.BowlRating = '';
          payload.Wickets = '';
        }
        if (payload.CricHeroesAccount !== 'Yes') payload.CricHeroesUserId = '';
        if (payload.MedicalConditions !== 'Yes') payload.MedicalDetails = '';

        // The photo is always the employee's profile photo; the API needs it as a file.
        const imageToSend = profilePhotoUrl ? await fileFromUrl(profilePhotoUrl) : null;

        await registerPlayer(payload, imageToSend);

        toast.success('Registration submitted. Good luck!');
        // Captured before resetForm() clears it - the confirmation screen reads the
        // record back from the API using this address.
        setRegisteredEmail(values.EmailId);
        resetForm();
        setHypeIndex(null);
        setHypeError('');
        setIsRegistered(true);
      } catch (error) {
        const status = error.response?.status;
        const message = extractErrorText(error);

        if (status === 409) {
          const lowered = message.toLowerCase();
          if (lowered.includes('t-shirt') || lowered.includes('tshirt')) {
            setFieldError('TShirtName', message);
          } else if (lowered.includes('email')) {
            setFieldError('EmailId', message);
          }
          toast.error(message || 'This registration conflicts with an existing one.');
          return;
        }

        const fieldErrors = extractFieldErrors(error, FIELD_NAMES);
        if (Object.keys(fieldErrors).length) {
          Object.entries(fieldErrors).forEach(([field, text]) => setFieldError(field, text));
          toast.error('Please correct the highlighted fields.');
          return;
        }

        if (message) {
          toast.error(message);
          if (message.toLowerCase().includes('closed')) {
            setRegistrationWindow((prev) => ({ ...prev, open: false }));
          }
          return;
        }

        toast.error('Something went wrong submitting your registration. Please try again.');
      }
    },
  });

  // The profile API resolves after first paint, so merge anything it adds - but only
  // into fields the user has not filled or touched, so typing is never clobbered.
  useEffect(() => {
    Object.entries(prefilledValues).forEach(([field, value]) => {
      if (value && !formik.values[field] && !formik.touched[field]) {
        formik.setFieldValue(field, value, false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledValues]);

  // Check the employee's own email once it is known. Their email cannot be changed here,
  // so finding out at submit time would leave them no way forward.
  useEffect(() => {
    const email = prefilledValues.EmailId;
    if (!email || !LOCKED_FIELDS.includes('EmailId')) return;

    let cancelled = false;
    setRegistrationChecked(false);
    checkEmailAvailable(email).then(({ available }) => {
      if (cancelled) return;
      if (!available) {
        setRegisteredEmail(email);
        setAlreadyRegistered(true);
      }
      setRegistrationChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [prefilledValues.EmailId]);

  // Once the employee is known to be registered, fetch what they submitted. A 404 is the
  // API saying "nobody is registered with that email in the current season" - an answer,
  // not a failure - so it reads differently from a lookup that actually broke.
  const loadRegisteredPlayer = useCallback(async () => {
    if (!registeredEmail) return;

    setPlayerLoading(true);
    setPlayerError('');
    setRegisteredPlayer(null);
    try {
      const player = await fetchPlayerByEmail(registeredEmail);
      if (player) {
        setRegisteredPlayer(player);
      } else {
        setPlayerError('We could not find your submitted details for the current season.');
      }
    } catch (error) {
      setPlayerError(
        extractIntegrationError(error) ||
          'Could not load your registration details. Please try again shortly.'
      );
    } finally {
      setPlayerLoading(false);
    }
  }, [registeredEmail]);

  useEffect(() => {
    if (!alreadyRegistered && !isRegistered) return;
    loadRegisteredPlayer();
  }, [alreadyRegistered, isRegistered, loadRegisteredPlayer]);

  const registeredPhoto = useMemo(() => extractPlayerPhoto(registeredPlayer), [registeredPlayer]);

  // The submitted record, grouped for scanning rather than listed flat - this renders in
  // the story viewer, which is phone-width, so a single 20-row list reads as a wall.
  // Waits for the lookups: the answers come back as externalIds that have to be resolved
  // to the label the player actually picked. Empty answers are dropped rather than shown
  // blank, which is also how the batting / bowling halves stay out for a role that does
  // neither, and how a whole section disappears when nothing in it was answered.
  const registeredSections = useMemo(() => {
    if (!registeredPlayer || !lookups.roles.length) return [];

    const values = mapPlayerToFormValues(registeredPlayer, lookups, FIELD_NAMES);
    const labelOf = (options, value) =>
      options.find((option) => String(option.value) === String(value))?.label || '';

    const roleLabel = labelOf(lookups.roles, values.PlayerRoleExternalId);
    const role = roleLabel.toLowerCase();
    const rated = (value) => (String(value ?? '').trim() === '' ? '' : `${value}/5`);

    return [
      {
        title: 'Player',
        rows: [
          { label: 'Name', value: [values.FirstName, values.LastName].filter(Boolean).join(' ') },
          { label: 'Official email', value: values.EmailId },
          { label: 'Mobile number', value: values.PhoneNo },
          { label: 'Age', value: values.Dob },
          { label: 'Gender', value: values.Gender },
          { label: 'Work location', value: labelOf(lookups.workLocations, values.WorkLocationExternalId) },
        ],
      },
      {
        title: 'On the field',
        rows: [
          { label: 'Role', value: roleLabel },
          ...(BAT_ROLES.includes(role)
            ? [
                { label: 'Batting style', value: labelOf(lookups.battingStyles, values.BattingStyleExternalId) },
                { label: 'Batting rating', value: rated(values.BatRating) },
                { label: 'Highest runs (last 3 months)', value: values.Runs },
              ]
            : []),
          ...(BALL_ROLES.includes(role)
            ? [
                { label: 'Bowling style', value: labelOf(lookups.bowlingStyles, values.BowlingStyleExternalId) },
                { label: 'Bowling rating', value: rated(values.BowlRating) },
                { label: 'Highest wickets (last 3 months)', value: values.Wickets },
              ]
            : []),
          { label: 'Played GPL before', value: labelOf(EXPERIENCE_YEARS, values.ExperienceYears) },
        ],
      },
      {
        title: 'Kit',
        rows: [
          { label: 'T-shirt size', value: labelOf(lookups.tShirtSizes, values.TshirtSizeExternalId) },
          { label: 'Jersey number', value: values.TShirtNumber },
          { label: 'Name on jersey', value: values.TShirtName },
        ],
      },
      {
        title: 'More',
        rows: [
          { label: 'History on Cricheroes', value: values.CricHeroesAccount },
          ...(values.CricHeroesAccount === 'Yes'
            ? [{ label: 'Cricheroes number', value: values.CricHeroesUserId }]
            : []),
          { label: 'Medical conditions', value: values.MedicalConditions },
          ...(values.MedicalConditions === 'Yes'
            ? [{ label: 'Medical details', value: values.MedicalDetails, wide: true }]
            : []),
          // Free text runs to FREE_TEXT_MAX characters, so it gets its own full-width block rather
          // than being squeezed into the right-hand column.
          { label: 'Cricketing journey', value: values.CricketJourney, wide: true },
        ],
      },
    ]
      .map((section) => ({
        ...section,
        rows: section.rows.filter((row) => String(row.value ?? '').trim() !== ''),
      }))
      .filter((section) => section.rows.length > 0);
  }, [registeredPlayer, lookups]);

  // Deliberately not theme.palette.text.secondary - this app overrides it to a blue that
  // makes every label read as a link (the same reason LOCKED_FIELD_TINT is hardcoded).
  const DETAIL_LABEL_COLOR =
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.55)';

  const currentRoleName = roleNameFor(formik.values.PlayerRoleExternalId);
  const canBat = BAT_ROLES.includes(currentRoleName);
  const canBall = BALL_ROLES.includes(currentRoleName);

  const handleEmailBlur = async (event) => {
    formik.handleBlur(event);
    const email = event.target.value?.trim();
    if (!email || formik.errors.EmailId) return;

    const { available, message } = await checkEmailAvailable(email);
    if (available) return;

    // A locked email comes from the employee record, so a field error is unactionable -
    // surface it as "you are already registered" instead.
    if (isLocked('EmailId')) {
      setRegisteredEmail(email);
      setAlreadyRegistered(true);
    } else {
      formik.setFieldError('EmailId', message);
    }
  };

  // Resets back to initialValues, so the locked employee fields keep their values.
  const handleReset = () => {
    formik.resetForm();
    setHypeIndex(null);
    setHypeError('');
  };

  // This form renders inside the story viewer (SwipeableGrid), which listens for
  // swipes to change or close the story. Without this the form cannot be scrolled.
  const stopTouchPropagation = (event) => event.stopPropagation();

  // A field is locked once the employee record has supplied its value. If the record
  // has no value it stays editable, so a gap in the profile can never block registration.
  const isLocked = (name) => LOCKED_FIELDS.includes(name) && !!prefilledValues[name];

  const LOCKED_FIELD_TINT =
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.025)';

  const fieldProps = (name, maxLength) => {
    const locked = isLocked(name);
    return {
      name,
      value: formik.values[name],
      onChange: formik.handleChange,
      onBlur: formik.handleBlur,
      error: formik.touched[name] && !!formik.errors[name],
      helperText: (formik.touched[name] && formik.errors[name]) || ' ',
      fullWidth: true,
      size: 'small',
      InputLabelProps: { style: { fontSize: variants.fontSize.SUB_HEADING } },
      // readOnly rather than disabled: the value still submits and stays legible.
      InputProps: locked ? { readOnly: true } : undefined,
      sx: locked
        ? {
            // Deliberately not theme.palette.action.hover - this app overrides it to a
            // heavy #E0E0E0 (#999 in dark mode). A faint overlay is enough to read as
            // "locked" without making the value look greyed out.
            '& .MuiInputBase-root': { backgroundColor: LOCKED_FIELD_TINT },
            '& .MuiInputBase-input': { cursor: 'default' },
          }
        : undefined,
      ...(maxLength ? { inputProps: { maxLength } } : {}),
    };
  };

  const selectField = (name, label, options, disabled = false) => (
    <TextField
      select
      label={label}
      disabled={disabled}
      SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
      {...fieldProps(name)}
    >
      {options.length === 0 && (
        <MenuItem disabled value="">
          Loading…
        </MenuItem>
      )}
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  const radioRow = (name, label, options, lockedNote) => {
    // The whole row is disabled when locked, not just the option that was not chosen.
    // Previously only the other option was disabled while the selected one still rendered
    // as a live control, so the field looked editable and clicking simply did nothing with
    // no explanation (GPL-04) - hence the note below it.
    //
    // Only ever true for Gender, and only when the employee record actually supplied one:
    // isLocked requires a prefilled value, so a gap in the profile leaves the field
    // editable rather than making registration impossible.
    const locked = isLocked(name);
    return (
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: variants.fontSize.HEADING, fontWeight: 500, mb: 0.5 }}>{label}</Typography>
        <RadioGroup
          row
          name={name}
          value={formik.values[name]}
          onChange={locked ? undefined : formik.handleChange}
          sx={
            locked
              ? {
                  // The same faint tint the locked text fields use, so 'locked' looks the
                  // same everywhere on this form rather than inventing a second style.
                  backgroundColor: LOCKED_FIELD_TINT,
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: 'divider',
                  width: 'fit-content',
                  px: 1,
                  py: 0.25,
                }
              : undefined
          }
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              disabled={locked}
              control={
                <Radio
                  size="small"
                  // Grey rather than accent once locked: a brightly coloured radio next to
                  // greyed labels still reads as a live control you could click.
                  sx={{ '&.Mui-checked': { color: locked ? 'text.disabled' : ACCENT } }}
                />
              }
              label={<Typography sx={{ fontSize: variants.fontSize.SUB_HEADING }}>{option.label}</Typography>}
            />
          ))}
        </RadioGroup>
        {locked && lockedNote && (
          <FormHelperText sx={{ mt: -0.5 }}>{lockedNote}</FormHelperText>
        )}
        {formik.touched[name] && formik.errors[name] && <FormHelperText error>{formik.errors[name]}</FormHelperText>}
      </Box>
    );
  };

  // "Rate yourself as a Batter / Bowler" - a 1-5 slider with lowest/highest end labels,
  // shown only once the matching style has been chosen.
  const ratingSlider = (name, label) => (
    // Extra bottom margin: MUI positions the mark labels absolutely below the rail
    // without growing the slider, so without it they collide with the next field.
    <Box sx={{ px: 1, mb: 2.5 }}>
      <Typography sx={{ fontSize: variants.fontSize.SUB_HEADING, color: 'text.secondary' }}>{label}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: variants.fontSize.TEXT, color: 'text.secondary' }}>lowest</Typography>
        <Typography sx={{ fontSize: variants.fontSize.TEXT, color: 'text.secondary' }}>highest</Typography>
      </Box>
      <Slider
        name={name}
        value={formik.values[name] === '' ? 1 : Number(formik.values[name])}
        onChange={(_event, value) => formik.setFieldValue(name, value)}
        min={1}
        max={5}
        step={1}
        marks={RATING_MARKS}
        valueLabelDisplay="auto"
        sx={{
          color: ACCENT,
          // Match the 'lowest' / 'highest' captions above, so the scale reads as one set.
          '& .MuiSlider-markLabel': {
            fontSize: variants.fontSize.TEXT,
            color: 'text.secondary',
          },
        }}
      />
    </Box>
  );

  const shell = (children) => (
    <Box
      onTouchStart={stopTouchPropagation}
      onTouchMove={stopTouchPropagation}
      sx={{
        width: '100%',
        // The story viewer does not bound its children's height, so cap it here to
        // keep the form scrollable instead of overflowing off the dialog.
        flex: 1,
        minHeight: 0,
        height: { xs: 'calc(100vh - 64px)', md: '100%' },
        overflowY: 'auto',
        backgroundColor: '#F7F7F7',
        px: { xs: 1.5, sm: 3 },
        py: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={gplIcon}
            sx={{ width: 40, height: 40, backgroundColor: 'white', img: { objectFit: 'contain' } }}
          />
          <Typography sx={{ fontSize: { xs: '1.15rem', sm: '1.5rem' }, fontWeight: 600 }}>
            GPL Player Registration
          </Typography>
        </Box>
        <Typography sx={{ fontSize: variants.fontSize.SUB_HEADING, color: 'text.secondary' }}>
          Register Your Victory !!
        </Typography>
      </Box>
      {children}
    </Box>
  );

  const card = (children) => (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: variants.BOX_SHADOW,
        p: { xs: 2, sm: 3 },
        maxWidth: '900px',
        mx: 'auto',
      }}
    >
      {children}
    </Box>
  );

  if (isLoading) {
    return shell(
      card(
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress sx={{ color: ACCENT }} />
          <Typography sx={{ fontSize: variants.fontSize.SUB_HEADING }}>Loading registration form…</Typography>
        </Box>
      )
    );
  }

  if (loadError) {
    return shell(
      card(
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {loadError}
          </Alert>
          <Button onClick={loadFormData} sx={{ color: ACCENT, textTransform: 'none' }}>
            Retry
          </Button>
        </Box>
      )
    );
  }

  // One screen for every "not open to you yet" case below.
  const comingSoonScreen = (message, onBack) =>
    shell(
      card(
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4 }}>
          <Box
            component="img"
            src={comingSoonGif}
            alt=""
            sx={{ width: { xs: 200, sm: 250 }, height: 'auto', borderRadius: '12px' }}
          />
          <Typography sx={{ fontSize: variants.fontSize.LARGE, fontWeight: 600, textAlign: 'center' }}>
            Registration opens shortly
          </Typography>
          <Typography sx={{ fontSize: variants.fontSize.SUB_HEADING, color: 'text.secondary', textAlign: 'center' }}>
            {message}
          </Typography>
          {onBack && (
            <Button onClick={onBack} sx={{ color: ACCENT, textTransform: 'none', mt: 1 }}>
              Choose a different option
            </Button>
          )}
        </Box>
      )
    );

  // Their email is already on a registration and cannot be edited here, so there is
  // nothing to fill in - say so plainly instead of showing an unfixable field error.
  // Shared by both confirmation screens - someone who registered a minute ago and
  // someone who registered last week are shown the same record, read back from the
  // API rather than from whatever the form happened to hold.
  const submittedDetails = () => (
    <>
      {playerLoading &&
        card(
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress sx={{ color: ACCENT }} />
            <Typography sx={{ fontSize: variants.fontSize.SUB_HEADING }}>
              Loading your registration details…
            </Typography>
          </Box>
        )}

      {/* Not a failure from the player's side - the card above still stands - so this
          stays informational, and retryable. */}
      {!playerLoading &&
        !!playerError &&
        card(
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 2 }}>
            <Alert severity="info" sx={{ width: '100%' }}>
              {playerError}
            </Alert>
            <Button onClick={loadRegisteredPlayer} sx={{ color: ACCENT, textTransform: 'none' }}>
              Try again
            </Button>
          </Box>
        )}

      {!playerLoading &&
        !playerError &&
        registeredSections.length > 0 &&
        card(
          <>
            <Typography sx={{ fontSize: variants.fontSize.LARGE, fontWeight: 600 }}>
              Your submitted details
            </Typography>
            <Typography
              sx={{ fontSize: variants.fontSize.SUB_HEADING, color: DETAIL_LABEL_COLOR, mb: 2 }}
            >
              Shown as you submitted them, and not editable here.
            </Typography>

            {/* Only when there is a real photo - an empty Avatar is a grey placeholder
                that reads as a broken image rather than as "no photo". */}
            {!!(registeredPhoto || profilePhotoUrl) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar
                  src={registeredPhoto || profilePhotoUrl}
                  sx={{
                    width: 88,
                    height: 88,
                    border: `3px solid ${ACCENT}`,
                    img: { objectFit: 'cover', objectPosition: 'top' },
                  }}
                />
              </Box>
            )}

            {registeredSections.map((section) => (
              <Box key={section.title} sx={{ mb: 2.5, '&:last-of-type': { mb: 0 } }}>
                <Typography
                  sx={{
                    fontSize: variants.fontSize.TEXT,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: ACCENT,
                    mb: 0.5,
                  }}
                >
                  {section.title}
                </Typography>

                {section.rows.map((row) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: 'flex',
                      // Label and value share a line so the list stays compact; the free
                      // text blocks stack instead, where a right-hand column would leave
                      // a two-word ribbon down the side of the card.
                      flexDirection: row.wide ? 'column' : 'row',
                      alignItems: row.wide ? 'stretch' : 'baseline',
                      justifyContent: 'space-between',
                      gap: row.wide ? 0.25 : 2,
                      py: 0.85,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-of-type': { borderBottom: 'none', pb: 0 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: variants.fontSize.SUB_HEADING,
                        color: DETAIL_LABEL_COLOR,
                        flexShrink: 0,
                      }}
                    >
                      {row.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: variants.fontSize.SUB_HEADING,
                        fontWeight: 500,
                        textAlign: row.wide ? 'left' : 'right',
                        wordBreak: 'break-word',
                        minWidth: 0,
                      }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </>
        )}
    </>
  );

  // Anyone with a registration sees it, whatever the window says - a closed auction is
  // no reason to hide what they submitted, and this is the screen they came for.
  if (alreadyRegistered) {
    return shell(
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {card(
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4 }}>
            <CheckCircle sx={{ fontSize: 56, color: ACCENT }} />
            <Typography sx={{ fontSize: variants.fontSize.LARGE, fontWeight: 600, textAlign: 'center' }}>
              You're already registered
            </Typography>
            <Typography sx={{ fontSize: variants.fontSize.SUB_HEADING, color: DETAIL_LABEL_COLOR, textAlign: 'center' }}>
              A GPL registration already exists for {registeredEmail || prefilledValues.EmailId}.
              Contact {GPL_CONTACT} to change your details.
            </Typography>
          </Box>
        )}
        {submittedDetails()}
      </Box>
    );
  }

  if (isRegistered) {
    return shell(
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {card(
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4 }}>
            <CheckCircle sx={{ fontSize: 56, color: '#2E7D32' }} />
            <Typography sx={{ fontSize: variants.fontSize.LARGE, fontWeight: 600, textAlign: 'center' }}>
              You're registered!
            </Typography>
            <Typography sx={{ fontSize: variants.fontSize.SUB_HEADING, color: DETAIL_LABEL_COLOR, textAlign: 'center' }}>
              Your GPL player registration has been submitted successfully. Contact {GPL_CONTACT} to
              change your details.
            </Typography>
          </Box>
        )}
        {submittedDetails()}
      </Box>
    );
  }

  // `open` is the master switch - when it is off nobody registers, whatever the
  // per-gender flags say. Checked only after the two screens above, so a player who
  // already has a registration is never told the auction is shut instead of being shown
  // their own entry.
  if (!registrationWindow?.open) {
    // Their email is known but the answer is not in yet. Showing "opens shortly" here
    // would be replaced a moment later by their own registration, so wait instead.
    if (prefilledValues.EmailId && !registrationChecked) {
      return shell(
        card(
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress sx={{ color: ACCENT }} />
            <Typography sx={{ fontSize: variants.fontSize.SUB_HEADING }}>Checking your registration…</Typography>
          </Box>
        )
      );
    }
    return comingSoonScreen("We're still warming up. Player registration will open here soon.");
  }

  // Each auction opens on its own schedule, so a player can be shut out while the other
  // one runs. Gender comes from the employee record when HR holds one (locked, so this
  // is final); otherwise it is whatever the player has picked so far and they can go
  // back and change it. Flags are compared against `false` explicitly so a missing key
  // in the response leaves registration open rather than silently closing it.
  //
  // Deliberately last: someone who already has a registration should be told that,
  // rather than that their auction has not opened yet.
  const chosenGender = formik.values.Gender;
  const genderClosed =
    (chosenGender === 'Male' && registrationWindow?.male === false) ||
    (chosenGender === 'Female' && registrationWindow?.female === false);

  if (genderClosed) {
    return comingSoonScreen(
      `The ${chosenGender.toLowerCase()} auction is still warming up. Registration will open here soon.`,
      isLocked('Gender') ? undefined : () => formik.setFieldValue('Gender', '')
    );
  }

  return shell(
    card(
      <Box component="form" onSubmit={formik.handleSubmit} noValidate>
        {/* Profile photo - taken from the employee record, not editable here. */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Avatar
            src={profilePhotoUrl}
            sx={{
              width: 110,
              height: 110,
              border: `4px solid ${ACCENT}`,
              backgroundColor: '#E0E0E0',
              img: { objectFit: 'cover', objectPosition: 'top' },
            }}
          />
        </Box>

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField label="First Name *" {...fieldProps('FirstName', 20)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Last Name *" {...fieldProps('LastName', 20)} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Official Email Address *"
              type="email"
              {...fieldProps('EmailId', 80)}
              onBlur={handleEmailBlur}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Mobile Number *" type="tel" {...fieldProps('PhoneNo', 10)} />
          </Grid>

          <Grid item xs={12} sm={6}>
            {/* The API calls this field Dob, but it takes an integer age in years. */}
            <TextField label="Age *" {...fieldProps('Dob', 2)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            {radioRow('Gender', 'Gender*:', genderOptions, 'Taken from your employee record.')}
          </Grid>

          <Grid item xs={12} sm={6}>
            {selectField('TshirtSizeExternalId', 'T-Shirt Size *', lookups.tShirtSizes)}
          </Grid>
          <Grid item xs={12} sm={6}>
            {selectField('WorkLocationExternalId', 'Work Location *', lookups.workLocations)}
          </Grid>

          <Grid item xs={12} sm={6}>
            {selectField('PlayerRoleExternalId', 'Role *', lookups.roles)}
          </Grid>
          <Grid item xs={12} sm={6} />

          {/* Batting half - style, self-rating and runs, all shown only when the chosen
              role bats. Hidden rather than disabled: a greyed-out '*' field reads as a
              mandatory question the player is being blocked from answering. */}
          {canBat && (
            <Grid item xs={12} sm={6}>
              {selectField('BattingStyleExternalId', 'Batting Style *', lookups.battingStyles)}
              {formik.values.BattingStyleExternalId && ratingSlider('BatRating', 'Rate yourself as a Batter')}
            </Grid>
          )}
          {canBat && (
            <Grid item xs={12} sm={6}>
              <TextField label="Highest Run Score in Last 3 Months *" {...fieldProps('Runs', 3)} />
            </Grid>
          )}

          {/* Bowling half - the same, for roles that bowl. A wicketkeeper batsman sees
              none of this. */}
          {canBall && (
            <Grid item xs={12} sm={6}>
              {selectField('BowlingStyleExternalId', 'Bowling Style *', lookups.bowlingStyles)}
              {formik.values.BowlingStyleExternalId && ratingSlider('BowlRating', 'Rate yourself as a Bowler')}
            </Grid>
          )}
          {canBall && (
            <Grid item xs={12} sm={6}>
              <TextField label="Highest Wicket Taken in Last 3 Months *" {...fieldProps('Wickets', 3)} />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField label="Jersey Number *" {...fieldProps('TShirtNumber', 3)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Name on Jersey *" {...fieldProps('TShirtName', 20)} />
          </Grid>

          <Grid item xs={12} sm={6}>
            {selectField('ExperienceYears', 'Have you ever played GPL In Past? *', EXPERIENCE_YEARS)}
          </Grid>
          <Grid item xs={12} sm={6} />

          <Grid item xs={12} sm={formik.values.CricHeroesAccount === 'Yes' ? 6 : 12}>
            {radioRow('CricHeroesAccount', 'Do you have any history on Cricheroes?*:', [
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ])}
          </Grid>
          {formik.values.CricHeroesAccount === 'Yes' && (
            <Grid item xs={12} sm={6}>
              <TextField label="Registered Phone Number *" type="tel" {...fieldProps('CricHeroesUserId', 10)} />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Brief your cricketing Journey: *"
              multiline
              rows={3}
              {...fieldProps('CricketJourney', FREE_TEXT_MAX)}
              helperText={
                (formik.touched.CricketJourney && formik.errors.CricketJourney) ||
                `${formik.values.CricketJourney.length}/${FREE_TEXT_MAX}`
              }
            />
          </Grid>

          <Grid item xs={12} sm={formik.values.MedicalConditions === 'Yes' ? 6 : 12}>
            {radioRow('MedicalConditions', 'Do you have any medical conditions that we should be aware of?*:', [
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ])}
          </Grid>
          {formik.values.MedicalConditions === 'Yes' && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Medical Details *"
                multiline
                rows={3}
                {...fieldProps('MedicalDetails', FREE_TEXT_MAX)}
                helperText={
                  (formik.touched.MedicalDetails && formik.errors.MedicalDetails) ||
                  `${formik.values.MedicalDetails.length}/${FREE_TEXT_MAX}`
                }
              />
            </Grid>
          )}

          {/* Hype rating - kept out of the submitted payload, but still required: submit is
              refused without it, so it carries the same '*' as every other mandatory field.
              Placed against the question mark like the radio rows above, leaving the
              decorative emojis at the end. */}
          <Grid item xs={12}>
            <Typography sx={{ fontSize: variants.fontSize.HEADING, fontWeight: 500, mb: 0.5 }}>
              How hyped are you for the GPL Cricket Showdown?* 🤩🏏
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, sm: 3 } }}>
              {HYPE_EMOJIS.map((option, index) => (
                <Tooltip key={option.emoji} title={option.tooltip} arrow>
                  <Box
                    component="span"
                    onClick={() => {
                      setHypeIndex(index);
                      setHypeError('');
                    }}
                    sx={{
                      cursor: 'pointer',
                      fontSize: { xs: '1.6rem', sm: '2rem' },
                      lineHeight: 1,
                      filter: hypeIndex === index ? 'none' : 'grayscale(100%)',
                      opacity: hypeIndex === index ? 1 : 0.55,
                      transform: hypeIndex === index ? 'scale(1.2)' : 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': { opacity: 1, filter: 'none' },
                    }}
                  >
                    {option.emoji}
                  </Box>
                </Tooltip>
              ))}
            </Box>
            {hypeError && <FormHelperText error sx={{ textAlign: 'center' }}>{hypeError}</FormHelperText>}
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Reset form" arrow>
              <IconButton onClick={handleReset} disabled={formik.isSubmitting} sx={{ color: '#D32F2F' }}>
                <RestartAlt />
              </IconButton>
            </Tooltip>
            <Button
              type="submit"
              fullWidth
              disabled={formik.isSubmitting}
              startIcon={formik.isSubmitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
              sx={{
                backgroundColor: ACCENT,
                color: 'white',
                textTransform: 'none',
                fontSize: variants.fontSize.HEADING,
                borderRadius: '6px',
                py: 1,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
                '&.Mui-disabled': { backgroundColor: theme.palette.action.disabledBackground, color: 'white' },
              }}
            >
              {formik.isSubmitting ? 'Registering…' : 'Register'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    )
  );
};

export default GplRegistrationForm;
